﻿// #region: Class definition *********************************************************************/
/**
 * Manages form control(s) and its logic.
 */
window.formControls = new class {

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {

        // Track which requests are retries to prevent infinite loops
        const retryTracker = new WeakMap();

        // Setup CSRF safety for (jQuery) AJAX.
        $.ajaxPrefilter(async function (options, originalOptions, xhr) {
            // Don't add token to GET requests.
            if (!options.method || options?.method?.toUpperCase() !== 'GET') {
                xhr.setRequestHeader(antiforgeryHeaderName, core.getAntiForgeryToken());

                // Skip further handling if this is already a retry.
                if (retryTracker.has(originalOptions)) {
                    return;
                }

                // Store the original error callback if it exists.
                const originalErrorCallback = options.error;

                // Override the error callback.
                options.error = async function (jqXHR, textStatus, errorThrown) {
                    // Only retry for 400 errors that might be antiforgery related.
                    if (jqXHR.status === 400 && !retryTracker.has(originalOptions)) {
                        try {
                            // Try to get a fresh token.
                            await core.updateAntiForgeryToken();

                            // Clone the original options for the retry.
                            const retryOptions = $.extend(true, {}, originalOptions);

                            // Remove our custom error handler to avoid loops.
                            delete retryOptions.error;

                            // Mark this request as being retried.
                            retryTracker.set(retryOptions, true);

                            // Make a new AJAX request with the new token.
                            $.ajax(retryOptions)
                                .fail(function (retryJqXHR, retryTextStatus, retryErrorThrown) {
                                    // Call the original error callback if it exists.
                                    if (originalErrorCallback) {
                                        originalErrorCallback(retryJqXHR, retryTextStatus, retryErrorThrown);
                                    }
                                });

                            return;
                        }
                        catch (error) {
                            console.error("Error during AJAX retry:", error);
                        }
                    }

                    // Call the original error callback if it exists.
                    if (originalErrorCallback) {
                        originalErrorCallback(jqXHR, textStatus, errorThrown);
                    }
                };
            }
        });

        $.ajaxSetup({
            cache: false
        });

        this.questionTypes = {
            text: 0,
            checkbox: 1,
            dropdown: 2,
            matrix: 3,
            dateTime: 4,
            attachment: 5,
            radioButton: 6,
            inspectionPoint: 7,
            barcode: 8,
            formula: 9,
            drawing: 20
        };

        this.valueDropdownConfig = {
            dataTextField: "text",
            dataValueField: "value"
        };

        let originalEditor = jQuery.prototype.kendoEditor;
        let isSanitizedEditorInitialized = false;

        // Kendo and jQuery init.
        let { defaultTools } = kendo.ui.Editor;
        defaultTools.insertParagraph.options.shift = true;
        defaultTools.insertLineBreak.options.shift = false;

        kendo.ui.Editor.fn.options.pasteCleanup = {
            span: true,
            css: true,
            msAllFormatting: true
        };

        kendo.ui.DateTimePicker.fn.options.weekNumber = true;
        kendo.ui.DatePicker.fn.options.weekNumber = true;

        if (typeof resources.dateTimePickerMessages !== "undefined") {
            kendo.ui.DateTimePicker.fn.options.messages = resources.dateTimePickerMessages;
            kendo.ui.TimePicker.fn.options.messages = resources.dateTimePickerMessages;
            kendo.ui.Calendar.fn.options.messages.today = resources.dateTimePickerMessages.today;
        }

        kendo.ui.DropDownList.fn.options.noDataTemplate = "";
        kendo.ui.DropDownList.fn.options.animation = false;

        kendo.ui.Window.fn.options.animation = {
            open: {
                effects: "fade:in",
                duration: 140
            },
            close: {
                effects: "fade:out",
                duration: 140
            }
        };

        jQuery.prototype.kendoEditor = function (options) {
            if (!window.DOMPurify) {
                throw new Error("DOMPurify is required to use kendo Editor safely.");
            }

            if (!isSanitizedEditorInitialized) {
                kendo.ui.Editor.fn.options.deserialization = {
                    custom(html) {
                        // Returning an empty string can make Kendo ignore the output, so we always need to return something.
                        return core.sanitizeHtml(html) || '\ufeff';
                    }
                };

                isSanitizedEditorInitialized = true;
            }

            return originalEditor.call(this, options);
        };
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string}                        The string representation of this class.
     */
    toString() {
        return "[formControls]";
    }

    static toString() {
        return "[formControls]";
    }

    /** 
     * Identifies the version.
     * @returns {string}                        The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * If a link pasted is custom code (eg #:4 |Formulier link|#) that starts with #, the browser will automatically prepend current URL on copy.
     * We just want the code used.
     * @param {String} html                     The html to parse
     */
    cleanUpLinkWithHashtag(html) {
        // If link is custom code (eg #:4 |Formulier link|#) that starts with #, browser will automatically prepend current URL on copy.
        let currentUrl = window.location.href.

            // Encode to use in regex.
            replace(/(\/|\.|\?)/g, "\\$1").

            // Encode to match HTML encoded.
            replace(/&/g, "&amp;").

            // Match ids from other pages.
            replace(/[=]\d+/g, "=\\d+"),
            anchorLinkRegex = new RegExp("<a href=\"" + currentUrl + "(#[^\"]*)\"", "g");

        return html.replace(anchorLinkRegex, (match, groupAnchor) => {
            return "<a href=\"" + decodeURIComponent(groupAnchor) + "\"";
        });
    }

    /**
     * If an imagebrowser image is copied, the baseurl is included, this method replaces the baseurl with just a root path.
     * @param {String} html                     The html to parse the imagebrowser urls on.
     */
    cleanUpImageBrowserUrl(html) {
        // Replace the base url with '..' so images are independant of the base url.
        const imageBrowserRegex = new RegExp(`${baseUrl}/api/ImageBrowser/image`, "giu");
        return html.replace(imageBrowserRegex, "../api/ImageBrowser/image");
    }

    /**
     * Fill a provided kendo dropdownlist with either groups or users. 
     * @param {any} selectObject                The dropdown to fill.
     * @param {any} type                        The type of data to get, i.e. users or groups.
     * @param {any} options                     Any additional data.
     */
    createUserGroupDropdownList(selectObject, type, options) {
        options = options || {};
        let firstBlank = options.blank || false,
            path;

        if (type === "users") {
            let group = options.group || null,
                includeAnonymous = options.includeAnonymous || false;
            path = "/api/users?firstBlank=" + firstBlank + "&groupId=" + group + "&includeAnonymous=" + includeAnonymous;
        }
        else {
            path = "/api/groups?firstBlank=" + firstBlank;
        }

        return $(selectObject).kendoDropDownList({
            dataTextField: "text",
            dataValueField: "value",
            template: `<span class="k-default">#:data.text#</span>`,
            filter: "contains",
            value: options.selectedId,
            dataSource: {
                transport: {
                    read(options) {
                        $.getJSON(
                            baseUrl + path,
                            function (data) {
                                options.success(data);
                            }
                        ).fail(function (xhr) {
                            if (!core.supportsOffline() || xhr.readyState !== 0) {
                                return options.error(xhr);
                            }

                            var promise;
                            if (type === "groups") {
                                promise = leanformsNext.getOfflineGroups();
                            }
                            else if (type === "users" && group > 0) {
                                promise = leanformsNext.getOfflineUsersFromGroup(+group);
                            }
                            else {
                                promise = leanformsNext.getOfflineUsers();
                            }

                            promise.then((data) => {
                                const listData = firstBlank ? [{ text: "", value: "-1" }] : [];

                                // Using array.concat fails with Kendo bind.
                                data.forEach((item) => {
                                    listData.push({ text: item.text, value: item.value });
                                });
                                options.success(listData);
                            });
                        });
                    },
                    schema: {
                        model: {
                            id: "value"
                        }
                    }
                }
            }
        }).data("kendoDropDownList");
    }

    /**
     * 
     * @param {any} sourceObj
     * @param {any} sourceId
     * @param {any} isLoadingFormData
     * @param {any} isSetFilterPage
     */
    fillDependantList(sourceObj, sourceId, isLoadingFormData, isSetFilterPage) {
        try {
            let eventTargets = dropdownTriggers[sourceId];

            if (!eventTargets) {
                return;
            }

            let $sourceObj = $(sourceObj),
                sourceValue;

            if (sourceObj.type === "checkbox") {
                sourceValue = $sourceObj.prop("checked") ? "0" : "1";
            }
            else if (sourceObj.type === "radio") {
                sourceValue = $("input[name=\"" + sourceObj.name + "\"]:checked").val();
            }
            else {
                sourceValue = $sourceObj.val();
            }

            let selectedList = "";

            if (sourceObj.type === "select-one") {
                let selectedOption = $sourceObj.find("option:selected"),
                    listId = selectedOption.data("list");

                if (listId) {
                    selectedList = `${listId}#`;
                }
            }

            let compareValue = selectedList + sourceValue;

            eventTargets.forEach((targetId) => {
                let data = dropdownData[targetId];
                let newOptions = this.getDropDownOptions(data, compareValue, sourceId);
                let trg = document.getElementById((isSetFilterPage ? "" : "id_") + targetId);
                trg.innerHTML = "";

                let isReadOnly = trg.getAttribute("readonly");

                if (isReadOnly) {
                    let firstOption = newOptions[0];
                    newOptions = firstOption ? [firstOption] : [];
                }

                newOptions.forEach((option) => {
                    let el = document.createElement("option");
                    el.text = option.text;
                    el.value = option.value;
                    el.setAttribute("data-alt", option.alt || "");
                    el.setAttribute("data-list", option.listId || "");

                    trg.appendChild(el);
                });

                if (newOptions.length) {
                    trg.options[0].selected = true;
                }

                if (!isSetFilterPage) {
                    // TODO: why !isBlank?
                    if (!data.firstBlank) { changeDependentDate(trg); }

                    if (!isLoadingFormData) {
                        // TODO: call handleOnChange?
                        calc(targetId);
                    }
                }
            });
        }
        catch (e) {
            console.error(e);
        }
    }

    /**
     * 
     * @param {any} data
     * @param {any} value
     * @param {any} sourceId
     */
    getDropDownOptions(data, value, sourceId) {
        let newOptions = [],
            foundOptions = false;

        for (let i = 0; i < data.dependencies.length; i++) {
            let dependency = data.dependencies[i];

            if (dependency.questionId.toString() !== sourceId) {
                continue;
            }

            let isActive = validation.validateValueCompare({ leftValue: value, operator: dependency.operator, rightValue: dependency.value });
            if (isActive) {
                foundOptions = true;
                newOptions = dependency.options;
                break;
            }
        }

        if (!foundOptions) {
            newOptions = data.defaultOptions;
        }

        if (data.firstBlank) {
            newOptions = [{ value: "", text: "", alt: "", listId: "" }].concat(newOptions);
        }

        return newOptions;
    }

    /**
     * 
     * @param {any} colorPickerId
     * @param {any} full
     * @returns
     */
    getEditorTools(colorPickerId, full = false) {
        if (full) {
            return [
                "bold",
                "italic",
                "underline",
                "strikethrough",
                {
                    name: "fore-Color",
                    template: `
                        <div class="color-preview-tweaked" title="${resources.editor.foreColor}">
                            <div id="fore${colorPickerId}"></div>
                            <span class="k-icon k-font-icon k-i-foreground-color k-color-preview-icon overlay-icon"></span>
                        </div>`
                },
                {
                    name: "back-Color",
                    template: `
                        <div class="color-preview-tweaked" title="${resources.editor.backColor}">
                            <div id="back${colorPickerId}"></div>
                            <span class="k-icon k-font-icon k-i-droplet k-color-preview-icon overlay-icon"></span>
                        </div>`
                },
                "cleanFormatting",
                "createLink",
                "unlink",
                "insertImage",
                "insertUnorderedList",
                "insertOrderedList",
                "indent",
                "outdent",
                "subscript",
                "superscript",
                "justifyLeft",
                "justifyCenter",
                "justifyRight",
                "fontName",
                "fontSize",
                "createTable",
                "addColumnLeft",
                "addColumnRight",
                "addRowAbove",
                "addRowBelow",
                "deleteRow",
                "deleteColumn",
                "viewHtml"
            ];
        }

        return [
            {
                name: "fore-Color",
                template: `
                    <div class="color-preview-tweaked" title="${resources.editor.foreColor}">
                        <div id="fore${colorPickerId}"></div>
                        <span class="k-icon k-font-icon k-i-foreground-color k-color-preview-icon overlay-icon"></span>
                    </div>`
            },
            "bold",
            "italic",
            "underline",
            "cleanFormatting",
            "createLink",
            "unlink"
        ];
    }

    /**
     * 
     * @param {any} questionDropdown
     * @param {any} value
     * @param {any} operatorDropdown
     * @param {any} valueInput
     * @param {any} callback
     */
    getExtendedValues(questionDropdown, value, operatorDropdown, valueInput, callback) {
        let questionId = Number(questionDropdown.val());
        let isCurrentStep = questionId === -99 || isNaN(questionId),
            operator = operatorDropdown.val();
        let isContains = operator === "contains" || operator === "doesnotcontain";

        if (isCurrentStep) {
            value.wrapper.toggle(false);
            valueInput.toggle(true);

            valueInput.val("");
            value.dataSource.data([]);

            callback();

            return;
        }

        $.get({
            url: `${baseUrl}/api/questions/${questionId}/values/extended`,
            error: leanformsNext.onAjaxError,
            success(response) {
                var options = response.options;
                var isList = Array.isArray(options) && options.length > 0;
                var optionsIsSpecial = isList && (options[0].value === '#LOGGEDININFO1#' || options[0].value === '#LOGGEDININFO2#')

                if (optionsIsSpecial) {
                    isList = false;
                }

                var showText = isContains || !isList;

                value.wrapper.toggle(!showText);
                valueInput.toggle(showText);

                valueInput.val(optionsIsSpecial ? options[0].value : '');

                if (isList) {
                    options.forEach(function (option) {
                        if (option.list) {
                            option.value = option.list + "#" + option.value;
                        }
                    });
                }

                value.dataSource.data(isList ? options : []);

                if (isList) {
                    value.select(0);
                }

                callback(response.type, response.id);
            }
        });
    }

    /**
     * 
     * @param {String} url
     */
    goto(url) {
        const regexp = /^(file|ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
        const isUrl = regexp.test(url);

        if (isUrl) {
            window.open(url);
        }
        else {
            window.open("file:///" + url);
        }
    }

    /**
     * Hides the new tab checkbox on kendo editor link dialog.
     * @param {any} e
     * @returns
     */
    hideNewTabCheckbox(e) {
        if (e.name !== "createLink") {
            return;
        }

        setTimeout(function () {
            $("#k-editor-link-target").prop("checked", true);
            $("#k-editor-link-target").closest(".k-form-field").hide();
        }, 0);

        return;
    }

    /**
     * Cleans-up pasted content for paragraphs and divs with newlines and add target="_blank" to links.
     * @param {String} relativePath             A realtive path to use, defaults to '../'.
     * @returns {Object}                        The configuration for the editor.
     */
    getPasteCleanup(options) {
        return {
            custom: function (html) {
                // Replace paragraphs and divs with newlines. Needed because contents are display inline with flexbox in form, so paragraph doesn't work.
                var $html = $("<div>" + html + "</div>");
                $html
                    .find("p, div")
                    .after("<br>")
                    .contents()
                    .unwrap();

                html = $html.get(0).innerHTML;

                // Only allowed html.
                html = core.sanitizeHtml(html);

                if (options.addTargetBlank) {
                    html = html.replace(/<a[^>]*/, function (a) {
                        return a.replace(/target\s*=\s*['"][^"']*['"]/i, '') + ' target="_blank"';
                    });
                }

                if (options.removeEncodedImage) {
                    html = html.replace(/<img\s*src\s*=\s*['"]data:[^>]+>/, '');
                }

                html = formControls.cleanUpImageBrowserUrl(html);

                return html;
            }
        }
    }

    /**
     * 
     * @param {String} editorSelector
     * @param {String} pickerId
     */
    initializeBackColorPicker(editorSelector, pickerId) {
        this.#initializeColorPicker("back", editorSelector, pickerId);
    }

    /**
     * 
     * @param {String} editorSelector
     * @param {String} pickerId
     */
    initializeForeColorPicker(editorSelector, pickerId) {
        this.#initializeColorPicker("fore", editorSelector, pickerId);
    }

    /**
     * Move an item down in a list control.
     * @param {String} listId                   The id of the list.
     */
    moveDown(listId) {
        // Gets the item after the last selected item and moves it above the first selected item.
        var list = $("#" + listId);
        var selectedEls = list.find(":selected");
        var firstEl = selectedEls.first();
        var nextEl = selectedEls.last().next();

        if (!nextEl.length) {
            return;
        }

        var firstXmlItem = $(specific).find("item[id='" + firstEl.val() + "']");
        var nextXmlItem = $(specific).find("item[id='" + nextEl.val() + "']");
        
        firstXmlItem.before(nextXmlItem);
        firstEl.before(nextEl);
    }

    /**
     * Move an item up in a list control.
     * @param {String} listId                   The id of the list.
     */
    moveUp(listId) {
        // Gets the item before the first selected item and moves it after the last selected item.
        var list = $("#" + listId);
        var selectedEls = list.find(":selected");
        var lastEl = selectedEls.last();
        var previousEl = selectedEls.first().prev();

        if (!previousEl.length) {
            return;
        }

        var lastXmlItem = $(specific).find("item[id='" + lastEl.val() + "']");
        var previousXmlItem = $(specific).find("item[id='" + previousEl.val() + "']");

        lastXmlItem.after(previousXmlItem);
        lastEl.after(previousEl);
    }
    
    /**
     * Prevent deselection of multiselect option on clicking selected option,
     * which became possible in Chrome v125 but causes issues with our double click code. 
     * @param {MouseEvent} e                   The event.
     */
    preventMulitselectDeselect(e) {
        var $this = $(this);
        var selected = $this.find("option").filter(function () {
            return this.selected;
        });
        var previous = $this.data("previous-selected");

        if (selected.length === 1 && e.target.value === previous) {
            e.preventDefault();
        }

        $this.data("previous-selected", e.target.value);
    }

    /**
     * 
     * @param {any} dropDown
     * @param {any} value
     */
    selectValueOrFirst(dropDown, value) {
        if (value) {
            dropDown.value(value);

            if (!dropDown.value()) {
                dropDown.select(0);
            }
        }
        else {
            dropDown.select(0);
        }
    }

    /**
     * 
     * @param {any} contentHtml
     * @param {any} callback
     * @param {any} buttonText
     */
    showConfirmDialog(contentHtml, callback, buttonText, title, buttonClass = "button-danger") {
        $("#windowcontainer").append("<div id=\"confirmdialog\"></div>");
        
        if (!buttonText) {
            buttonText = resources.confirmDialogMessages.delete;
        }

        let confirmationWindow = $("#confirmdialog").kendoWindow({
            width: coreUI.getPopupWidth(400),
            title: title ?? resources.confirmDialogMessages.title,
            visible: false,
            modal: true,
            iframe: false,
            resizable: false,
            deactivate() {
                this.destroy();
                $("#windowcontainer").html("");
            }
        }).data("kendoWindow");

        const buttonIcon = buttonClass === 'button-danger' ? 'lf-icon-delete' : 'lf-icon-check';

        confirmationWindow.content(
            "<div class=\"popup-page popup-size-auto\">" +
                "<div class=\"popup-content\">" +
                    contentHtml +
                "</div>" +
                "<div class=\"popup-toolbar\">" +
                "  <button type=\"button\" id=\"btnCancel\" class=\"button button-default button-tall\"><span class=\"button-icon lf-icon-undo\"></span><span>" + resources.confirmDialogMessages.cancel + "</span></button>" +
                "  <button type=\"button\" id=\"btnOk\" class=\"button button-tall " + buttonClass + "\"><span class=\"button-icon " + buttonIcon + "\"></span><span>" + buttonText + "</span></button>" +
                "</div>" +
            "</div>"
        );

        $(confirmationWindow.element).find("#btnOk").on("click", () => {
            confirmationWindow.close();
            callback();
        });

        $(confirmationWindow.element).find("#btnCancel").on("click", () => {
            confirmationWindow.close();
        });

        confirmationWindow.center().open();
    }

    /**
     * 
     * Used by Form delete and DesignWorkflow rename.
     * @param {any} title
     * @param {any} bodyHtml
     * @param {any} callback
     */
    showOldConfirmDialog(title, bodyHtml, callback) {
        $("#windowcontainer").append("<div id=\"confirmdialog\" class=\"old-confirm\"></div>");

        var dialog = $("#confirmdialog").kendoWindow({
            title: title,
            width: coreUI.getPopupWidth(500),
            minHeight: coreUI.getPopupHeight(180),
            visible: false,
            modal: true,
            deactivate: function () {
                this.destroy();
            }
        }).data("kendoWindow");

        var confirmTemplate = kendo.template($("#confirmTemplate").html());
        dialog.content(confirmTemplate({ ConfirmText: bodyHtml }));

        $(dialog.element).find("#btnOk").on("click", function () {
            dialog.close();
            callback("OK", $("#confirmInput :input").serialize());
        });

        $(dialog.element).find("#btnCancel").on("click", function () {
            dialog.close();
            callback("CANCEL");
        });

        dialog.center().open();
    }

    /**
     * Used before full 'back-end' new UI conversion.
     * @param {any} contentHtml
     * @param {any} callback
     * @param {any} buttonText
     */
    showOldUIConfirmDialog(contentHtml, callback, buttonText, title, buttonClass = "btn-danger") {
        $("#windowcontainer").append("<div id=\"confirmdialog\"></div>");

        if (!buttonText) {
            buttonText = resources.confirmDialogMessages.delete;
        }

        let confirmationWindow = $("#confirmdialog").kendoWindow({
            width: coreUI.getPopupWidth(300),
            title: title ?? resources.confirmDialogMessages.title,
            visible: false,
            modal: true,
            iframe: false,
            resizable: false,
            deactivate() {
                this.destroy();
                $("#windowcontainer").html("");
            }
        }).data("kendoWindow");

        confirmationWindow.content(contentHtml +
            "<div class=\"confirm-buttons\">" +
            "  <button type=\"button\" id=\"btnCancel\" class=\"btn btn-default\"><span>" + resources.confirmDialogMessages.cancel + "</span></button>" +
            "  <button type=\"button\" id=\"btnOk\" class=\"btn " + buttonClass + "\"><span>" + buttonText + "</span></button>" +
            "</div>"
        );

        $(confirmationWindow.element).find("#btnOk").on("click", () => {
            confirmationWindow.close();
            callback();
        });

        $(confirmationWindow.element).find("#btnCancel").on("click", () => {
            confirmationWindow.close();
        });

        confirmationWindow.center().open();
    }

    /**
     * 
     * @param {any} operatorDropdown
     * @param {any} valueDropdown
     * @param {any} valueInput
     */
    toggleValueBox(operatorDropdown, valueDropdown, valueInput) {
        let valueDropdownKendo = valueDropdown.data("kendoDropDownList"),
            options = valueDropdownKendo.dataSource.data().length,
            operator = operatorDropdown.data("kendoDropDownList").value(),
            hasOptions = options > 0,
            isContains = operator === "contains" || operator === "doesnotcontain";

        let shouldShowDropdown = hasOptions && !isContains;
        valueDropdownKendo.wrapper.toggle(shouldShowDropdown);
        valueInput.toggle(!shouldShowDropdown);
    }

    /**
     * This functions expects jQuery XML.
     * @param {any} xmlData
     */
    xmlToString(xmlData) {
        try {
            let xmlString = undefined;

            if (window.ActiveXObject) {
                xmlString = xmlData[0].xml;
            }

            try {
                if (xmlString === undefined) {
                    var oSerializer = new XMLSerializer();
                    xmlString = oSerializer.serializeToString(xmlData[0]);
                }
            }
            catch (err) {
                if (xmlString === undefined) {
                    var oSerializer = new XMLSerializer();
                    xmlString = oSerializer.serializeToString(xmlData);
                }
            }

            return xmlString;
        }
        catch (error) {
            return `<error>${error.message}</error>`;
        }
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    #initializeColorPicker(type, editorSelector, pickerId) {
        const pickerSelector = "#" + type + pickerId;
        const action = type === "back" ? "backColor" : "foreColor";
        const palette = type === "back" ? editorColors.backColors : editorColors.foreColors;

        // When the editor is not an iframe (but contenteditable div), text selection is lost on click inside the picker.
        const editor = $(editorSelector).data("kendoEditor");
        const editorIsInline = $(editor.body).hasClass("k-editor-inline");
        let range;
        let preventOpen;

        const picker = $(pickerSelector).kendoColorPicker({
            messages: {
                apply: resources.editor.apply,
                cancel: resources.editor.cancel
            },
            preview: false,
            palette: palette,
            columns: 10,
            value: "rgba(255, 255, 255, 0)",
            view: "gradient",
            views: ["gradient", "palette"],
            open: function (e) {
                if (preventOpen) {
                    preventOpen = false;
                    return e.preventDefault();
                }

                range = editor.getRange();                
            },
            close: function () {
                range = null;
                preventOpen = false;
            },
            change: function (e) {
                var rangeCopy = range;

                // This somehow prevents enter from removing text.
                setTimeout(() => { 
                    if (editorIsInline) {
                        const selection = editor.getSelection();
                        selection.removeAllRanges();
                        selection.addRange(rangeCopy);
                        range = null;
                    }

                    editor.exec(action, { value: e.value });
                }, 0);
            }
        }).data("kendoColorPicker");

        // Allow setting last selected color by clicking left part.
        const leftButton = picker.wrapper.find(".k-input-inner");

        leftButton.on("click", function () {
            if (picker.color().match?.input === 'rgba(255, 255, 255, 0)') {
                // Don't prevent opening or set color of no color is set in picker.
                return;
            }

            preventOpen = true;

            if (editorIsInline && range) {
                const selection = editor.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
                range = null;
            }

            editor.exec(action, { value: picker.value() });
        });
    }
    // #endregion ********************************************************************************/
}();
// #endregion ************************************************************************************/
